import { useEffect, useRef } from 'react';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import { candidateKeys } from '@/features/candidates/keys';
import { duplicateKeys } from '@/features/duplicates/api';
import { sourceKeys } from '@/features/sources/api';
import { ruleKeys } from '@/features/rules/keys';
import { auditKeys } from '@/features/audit/keys';
import { toApiQuery, type RunsSearch } from './search';
import { IDLE_MS, isLive, pollInterval, triggerToApi } from './vocab';

export type Run = Schemas['ScrapeRunReportDto'];
export type RunPage = Schemas['ScrapeRunHistoryResponse'];
export type Spend = Schemas['SpendSummaryDto'];
export type ProviderSpend = Schemas['ProviderSpendDto'];

/** The API's largest page: the Inbox's week of runs, and any run in progress, in one request. */
export const RECENT_RUNS = 100;
export const SOURCE_RUNS_PAGE_SIZE = 10;

export const runKeys = {
  all: ['runs'] as const,
  recent: () => [...runKeys.all, 'recent'] as const,
  bySource: (sourceId: string, page: number) => [...runKeys.all, 'source', sourceId, page] as const,
  list: (search: RunsSearch) => [...runKeys.all, 'list', search] as const,
  count: () => [...runKeys.all, 'count'] as const,
  detail: (runId: string) => [...runKeys.all, 'detail', runId] as const,
  imports: () => [...runKeys.all, 'imports'] as const,
  spend: () => [...runKeys.all, 'spend'] as const,
};

/** How often a live run is read again. */
export const LIVE_MS = 3_000;
export const RECENT_MS = 10_000;
export const anyLive = (page: RunPage | undefined) => !!page?.items.some(isLive);

/** A page's override for a run list: its live rate, then once a minute while nothing is live. */
export const pollWhileIdle = (liveMs: number) => (query: { state: { data: RunPage | undefined } }) =>
  pollInterval(anyLive(query.state.data), liveMs, IDLE_MS);

/** The run history, newest first; polls while a run on the page is live. */
export const runsQuery = (search: RunsSearch) =>
  queryOptions({
    queryKey: runKeys.list(search),
    queryFn: () => unwrap(api.GET('/api/scraping/runs', { params: { query: toApiQuery(search) } })),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (anyLive(query.state.data) ? LIVE_MS : false),
  });

/** How many runs there are at all, to say what a filter hides. */
export const runCountQuery = () =>
  queryOptions({
    queryKey: runKeys.count(),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/runs', { params: { query: { pageSize: 1 } } })).then(
        (page) => page.totalCount,
      ),
  });

/** One run; polls while it is live, so its counts and failures stay live. */
export const runQuery = (runId: string) =>
  queryOptions({
    queryKey: runKeys.detail(runId),
    queryFn: () => unwrap(api.GET('/api/scraping/runs/{runId}', { params: { path: { runId } } })),
    refetchInterval: (query) => (query.state.data && isLive(query.state.data) ? LIVE_MS : false),
  });

/** The latest runs of every source, newest first. */
export const recentRunsQuery = () =>
  queryOptions({
    queryKey: runKeys.recent(),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/runs', { params: { query: { pageSize: RECENT_RUNS } } })),
    refetchInterval: (query) => (anyLive(query.state.data) ? RECENT_MS : false),
  });

/** AI spend over the API's window against the alert threshold and the ceiling, as the API judges them. */
export const spendQuery = () =>
  queryOptions({
    queryKey: runKeys.spend(),
    queryFn: () => unwrap(api.GET('/api/scraping/runs/spend')),
  });

/**
 * The spend summary, read again every 10 s while a run is going (a run records its cost page by
 * page); runs landing refresh it with the other run queries.
 */
export function useSpend() {
  const recent = useQuery(recentRunsQuery());
  return useQuery({ ...spendQuery(), refetchInterval: anyLive(recent.data) ? RECENT_MS : false });
}

/** One source's runs, newest first; polls while one is running so its counts stay live. */
export const sourceRunsQuery = (sourceId: string, page = 1) =>
  queryOptions({
    queryKey: runKeys.bySource(sourceId, page),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/runs', {
          params: {
            query: { sourceId, pageNumber: page, pageSize: SOURCE_RUNS_PAGE_SIZE },
          },
        }),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (anyLive(query.state.data) ? LIVE_MS : false),
  });

export const RECENT_IMPORTS = 5;

/** The latest imports, newest first. */
export const recentImportsQuery = () =>
  queryOptions({
    queryKey: runKeys.imports(),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/runs', {
          params: { query: { trigger: triggerToApi('import'), pageSize: RECENT_IMPORTS } },
        }),
      ),
  });

/** Run now. A 202 that started nothing is a refusal too, so it throws like a 409. */
export function useStartRun(sourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await unwrap(
        api.POST('/api/scraping/runs', { params: { query: { sourceId } } }),
      );
      const started = result.started.find((s) => s.sourceId === sourceId);
      if (!started) {
        const refused = result.refused.find((r) => r.sourceId === sourceId);
        throw new ApiError(409, { reason: refused?.reason ?? 'No run was started.' });
      }
      return started.runId;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: runKeys.all }),
        queryClient.invalidateQueries({ queryKey: sourceKeys.all }),
      ]),
  });
}

/** A landed run (finished, its rules decided) changes its source, the run lists, the candidates,
 * the duplicate groups, the rules and the audit trail. */
export function useRefreshWhenRunsFinish(runs: readonly Run[] | undefined) {
  const queryClient = useQueryClient();
  const live = !!runs?.some(isLive);
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) {
      for (const queryKey of [
        sourceKeys.all,
        runKeys.all,
        candidateKeys.all,
        duplicateKeys.all,
        ruleKeys.all,
        auditKeys.all,
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    }
    wasLive.current = live;
  }, [live, queryClient]);
}
